// Phase 164: Social Proof Notifications Component
// "John from Naples just requested a quote" style notifications

import { useState, useEffect, useRef } from "react";
import { MapPin, Clock, Star } from "lucide-react";

interface ActivityNotification {
  name: string;
  city: string;
  action: string;
  timeAgo: string;
}

// Realistic sample data (rotates through these)
const SAMPLE_ACTIVITIES: ActivityNotification[] = [
  { name: "John", city: "Naples", action: "requested a quote", timeAgo: "2 minutes ago" },
  { name: "Maria", city: "Fort Myers", action: "scheduled an inspection", timeAgo: "5 minutes ago" },
  { name: "Robert", city: "Cape Coral", action: "requested a quote", timeAgo: "8 minutes ago" },
  { name: "Sarah", city: "Sarasota", action: "left a 5-star review", timeAgo: "12 minutes ago" },
  { name: "Michael", city: "Bradenton", action: "requested a quote", timeAgo: "15 minutes ago" },
  { name: "Jennifer", city: "Tampa", action: "booked an estimate", timeAgo: "18 minutes ago" },
  { name: "David", city: "Bonita Springs", action: "requested a quote", timeAgo: "22 minutes ago" },
  { name: "Lisa", city: "Marco Island", action: "scheduled roof inspection", timeAgo: "25 minutes ago" },
  { name: "James", city: "Punta Gorda", action: "requested emergency repair", timeAgo: "28 minutes ago" },
  { name: "Patricia", city: "Venice", action: "left a 5-star review", timeAgo: "32 minutes ago" },
];

interface RecentActivityToastProps {
  enabled?: boolean;
  delay?: number; // Initial delay before first notification (ms)
  interval?: number; // Time between notifications (ms)
  duration?: number; // How long each notification shows (ms)
}

const RecentActivityToast = ({
  enabled = true,
  delay = 35000, // 35 seconds initial delay — reduced overlay spam
  interval = 45000, // 45 seconds between notifications
  duration = 5000, // 5 seconds display
}: RecentActivityToastProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<ActivityNotification | null>(null);
  const activityIndexRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    // Check if user dismissed notifications this session
    if (sessionStorage.getItem("obc_activity_dismissed") === "true") {
      return;
    }

    let showTimeout: NodeJS.Timeout;
    let hideTimeout: NodeJS.Timeout;
    let intervalId: NodeJS.Timeout;

    const showNotification = () => {
      const activity = SAMPLE_ACTIVITIES[activityIndexRef.current % SAMPLE_ACTIVITIES.length];
      setCurrentActivity(activity);
      setIsVisible(true);
      activityIndexRef.current += 1;

      // Hide after duration
      hideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, duration);
    };

    // Initial delay
    showTimeout = setTimeout(() => {
      showNotification();

      // Set up recurring interval
      intervalId = setInterval(showNotification, interval);
    }, delay);

    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
      clearInterval(intervalId);
    };
  }, [enabled, delay, interval, duration]);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem("obc_activity_dismissed", "true");
  };

  if (!isVisible || !currentActivity) return null;

  const isReview = currentActivity.action.includes("review");

  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-4 md:left-6 z-40 animate-in slide-in-from-bottom-4 duration-300"
      role="status"
      aria-live="polite"
    >
      <div className="bg-card rounded-lg shadow-lg border border-border p-4 max-w-sm flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isReview ? "bg-accent/20" : "bg-accent/10"
          }`}
        >
          {isReview ? (
            <Star className="h-5 w-5 text-accent fill-accent" />
          ) : (
            <MapPin className="h-5 w-5 text-accent" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            {currentActivity.name} from {currentActivity.city}
          </p>
          <p className="text-sm text-muted-foreground">
            {currentActivity.action}
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <Clock className="h-3 w-3" />
            {currentActivity.timeAgo}
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-muted-foreground hover:text-foreground text-xs"
          aria-label="Dismiss notification"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default RecentActivityToast;
